/**
 * NotificationBellButton — v221.4.0
 *
 * Shared bell-with-unread-badge used across the patient app.
 * Subscribes to useNotificationStore so the badge stays in sync
 * across every screen the user lands on.
 *
 * v221.4.0: Added `tone` prop so screens with navy (dark) headers —
 * CaseHome in particular — can use the same component without falling
 * back to the inline blue-badge implementation that previously diverged
 * from the canonical red-badge UX. Behavior:
 *   - tone="light" (default): navy bell on white/ice chrome
 *   - tone="dark":             white bell on navy chrome
 * The badge stays red #DC2626 in both tones (universal attention indicator,
 * matches MD Panel). On dark tone the badge border switches from white
 * to navy so it sits flush on the navy header.
 *
 * Variants:
 *   - default — used on top-level pages (Home / Cases / Visits / Profile)
 *   - compact — used inside case workflows (CompactHeader, CaseHome), slightly smaller
 */

import { useEffect } from 'react'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNotificationStore } from '../stores'
import releaseNotes from '../release-notes.json'
import { APP_VERSION } from '../utils/appVersion'
import { noticeNotes, readLastSeenVersion } from '../utils/whatsNew'

const BADGE_BG = '#DC2626'               // canonical red — universal attention indicator
const ICON_ACTIVE_LIGHT = '#15354F'      // brand navy (on light bg)
const ICON_MUTED_LIGHT = '#94A3B8'       // slate-400 (on light bg)
const ICON_ACTIVE_DARK = '#FFFFFF'       // white (on navy bg)
const ICON_MUTED_DARK = 'rgba(255,255,255,0.6)'  // soft white (on navy bg)
const NAVY = '#15354F'

export default function NotificationBellButton({ variant = 'default', tone = 'light', onClick }) {
  const navigate = useNavigate()
  const serverUnread = useNotificationStore((s) => s.unreadCount)
  // ★ s101: What's new is a notification — an unread notice adds one to the badge (no pop-up).
  //   Read from this device's last-seen version once per mount; WhatsNewNotice clears it on open.
  const whatsNewUnread = useNotificationStore((s) => s.whatsNewUnread)
  const setWhatsNewUnread = useNotificationStore((s) => s.setWhatsNewUnread)
  useEffect(() => {
    setWhatsNewUnread(noticeNotes(releaseNotes, APP_VERSION.replace(/^v/, ''), readLastSeenVersion()).unread)
  }, [setWhatsNewUnread])
  const unreadCount = serverUnread + (whatsNewUnread ? 1 : 0)
  const hasUnread = unreadCount > 0

  const isCompact = variant === 'compact'
  const isDark = tone === 'dark'
  const iconSize = isCompact ? 20 : 24
  const badgeMin = isCompact ? (unreadCount > 9 ? 16 : 14) : (unreadCount > 9 ? 18 : 16)
  const badgeFont = isCompact ? (unreadCount > 9 ? 9 : 8.5) : (unreadCount > 9 ? 10 : 9)

  const iconColor = hasUnread
    ? (isDark ? ICON_ACTIVE_DARK : ICON_ACTIVE_LIGHT)
    : (isDark ? ICON_MUTED_DARK : ICON_MUTED_LIGHT)

  // Badge border picks up the surrounding chrome so it reads as floating, not framed
  const badgeBorder = isDark ? NAVY : '#fff'

  const handleClick = () => {
    if (onClick) onClick()
    else navigate('/notifications')
  }

  return (
    <button
      onClick={handleClick}
      className={isCompact ? 'p-1.5 relative' : 'p-2 relative'}
      aria-label={hasUnread ? `Notifications (${unreadCount} unread)` : 'Notifications'}
    >
      <Bell
        size={iconSize}
        style={{ color: iconColor }}
        strokeWidth={2}
      />
      {hasUnread && (
        <span
          className="absolute flex items-center justify-center"
          style={{
            top: isCompact ? -2 : 0,
            right: isCompact ? -2 : 0,
            minWidth: badgeMin,
            height: badgeMin,
            padding: '0 4px',
            background: BADGE_BG,
            color: '#fff',
            border: `2px solid ${badgeBorder}`,
            borderRadius: 999,
            fontSize: badgeFont,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  )
}
